/**
 * 输入框里打「/」弹出的那份清单——与 Claude Code 的斜杠菜单同一件事。
 *
 * # 它只列「现在真能做的」
 *
 * 每一条都对着一个已有的接口或技能：档位是输入框旁那三颗按钮、「停下」是等回话
 * 那一行的那颗、技能是 GET /skills 里开着且在这种话头里说得动的、「改名」是
 * PATCH /conversations/{id}。**没有一条是画出来等后端的**：设计纠错清单 W17
 * 那个全局命令面板标着待新增（要 SearchIndex 与动作权限模型），这里刻意不做它——
 * 这只是输入框自己的一份清单，不搜索、不跳页。
 *
 * # 纯函数
 *
 * 这一份不碰 React：给它此刻的上下文与草稿，它回一份清单。判据都落在这里，
 * 界面那边只负责画与按键。用例也只需要它。
 */
import { checkValue } from '@npc/copy'

import { t } from '../../copy'
import { EFFORT_COPY, EFFORTS, type Effort } from './model-picker'
import type { LuluSkill } from './queries'

export type SlashAction =
  | { readonly kind: 'effort'; readonly effort: Effort }
  | { readonly kind: 'stop' }
  | { readonly kind: 'skill'; readonly utterance: string }
  | { readonly kind: 'new' }
  | { readonly kind: 'rename'; readonly title: string }
  /** 拨「这一条只出计划，别开工」那颗开关；on 是拨过去之后的状态。 */
  | { readonly kind: 'plan_only'; readonly on: boolean }

export interface SlashCommand {
  readonly id: string
  /** 已经渲染好的那一行字（模板或服务端给的名字）。 */
  readonly label: string
  readonly hint: string
  /** 过滤时除 label 之外还认的词。 */
  readonly keywords: readonly string[]
  readonly action: SlashAction
  /**
   * 现在按 Enter 动不动。要在「/改名」后面接东西的那种，没接时为假——
   * 列着但按不动，提示里说清接什么。
   */
  readonly ready: boolean
}

export interface SlashQuery {
  /** 「/」后面第一个空白之前的那段。 */
  readonly head: string
  /** 第一个空白之后的那段，去掉首尾空白。给「改名」这种要参数的。 */
  readonly arg: string
}

/**
 * 草稿是不是一条斜杠命令。**只认第一个字符是「/」**（与 Claude Code 一样），
 * 多行的不算——他要是在写一段话里提到路径，那不是在叫菜单。
 */
export function slashQuery(text: string): SlashQuery | null {
  if (!text.startsWith('/')) return null
  const rest = text.slice(1)
  if (rest.includes('\n')) return null
  const m = /^(\S*)\s*([\s\S]*)$/.exec(rest)
  return { head: (m?.[1] ?? '').trim(), arg: (m?.[2] ?? '').trim() }
}

export interface SlashContext {
  /** 挂了哪个项目。`null` = 总话头，据此挑「在这种话头里说得动」的技能。 */
  readonly projectId: string | null
  /** 真有一轮在跑（等回话那一行有「停下」）。假时不列「停下」。 */
  readonly running: boolean
  /** GET /skills 的投影。`null` / `undefined` = 读不到 / 还没读，技能那几条不列。 */
  readonly skills: readonly LuluSkill[] | null | undefined
  readonly canOpenNew: boolean
  readonly canRename: boolean
  /** 这一条草稿现在是不是「只出计划」。清单里那一条的字据此在开 / 关之间换。 */
  readonly planOnly: boolean
}

export function buildSlashCommands(ctx: SlashContext, arg: string): SlashCommand[] {
  const out: SlashCommand[] = []
  for (const effort of EFFORTS) {
    // 三档 id 同形（lulu_workbench.model.effort_*），与 composer-ui 里那三颗按钮同一个转换。
    const tier = t(EFFORT_COPY[effort] as 'lulu_workbench.model.effort_fast')
    out.push({
      id: `effort:${effort}`,
      label: t('lulu_workbench.slash.effort', { tier }),
      hint: t('lulu_workbench.slash.effort_hint'),
      keywords: ['想多深', '档', tier, 'effort', 'model', 'think'],
      action: { kind: 'effort', effort },
      ready: true,
    })
  }
  if (ctx.running) {
    out.push({
      id: 'stop',
      label: t('lulu_workbench.chat.stop'),
      hint: t('lulu_workbench.slash.stop_hint'),
      keywords: ['停', 'stop', 'esc'],
      action: { kind: 'stop' },
      ready: true,
    })
  }
  const scope = ctx.projectId === null ? 'account' : 'project'
  for (const skill of ctx.skills ?? []) {
    if (!skill.enabled || skill.scope !== scope) continue
    const utterance = skill.trigger.utterances[0]
    // 触发语是模板里的变量，值要过文案层那道口（不许 emoji、不许太长）。
    // 服务端给的本该都过得了；过不了就不列，别让一条技能把整份菜单画崩。
    if (utterance === undefined || utterance === '' || checkValue('utterance', utterance) !== null) continue
    out.push({
      id: `skill:${skill.key}`,
      label: skill.name,
      hint: t('lulu_workbench.slash.skill_hint', { utterance }),
      keywords: [skill.name, skill.key, ...skill.trigger.utterances],
      action: { kind: 'skill', utterance },
      ready: true,
    })
  }
  out.push({
    id: 'plan_only',
    label: t(ctx.planOnly ? 'lulu_workbench.slash.plan_only_off' : 'lulu_workbench.slash.plan_only_on'),
    hint: t('lulu_workbench.slash.plan_only_hint'),
    keywords: ['只出计划', '别开工', '计划', '开工', 'plan', 'no_auto_start'],
    action: { kind: 'plan_only', on: !ctx.planOnly },
    ready: true,
  })
  if (ctx.canOpenNew) {
    out.push({
      id: 'new',
      label: t('lulu_workbench.slash.new'),
      hint: t('lulu_workbench.slash.new_hint'),
      keywords: ['新', '话头', '会话', 'new'],
      action: { kind: 'new' },
      ready: true,
    })
  }
  if (ctx.canRename) {
    // 名字要能塞进模板（不许 emoji、有长度上限），塞不进的当成还没接名字。
    const title = checkValue('title', arg) === null ? arg : ''
    out.push({
      id: 'rename',
      label: t('lulu_workbench.slash.rename'),
      hint:
        title === ''
          ? t('lulu_workbench.slash.rename_hint_empty')
          : t('lulu_workbench.slash.rename_hint', { title }),
      keywords: ['改名', '名字', '标题', 'rename'],
      action: { kind: 'rename', title },
      ready: title !== '',
    })
  }
  return out
}

/** 按「/」后面那段挑。空 = 全列；大小写不敏感的子串匹配，认 label 与 keywords。 */
export function filterSlashCommands(
  commands: readonly SlashCommand[],
  head: string,
): SlashCommand[] {
  const q = head.trim().toLowerCase()
  if (q === '') return [...commands]
  return commands.filter((c) =>
    [c.label, ...c.keywords].some((k) => k.toLowerCase().includes(q)),
  )
}
